import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLinkedAccounts } from "../../context/LinkedAccountsContext";
import styles from "./AccountLink.module.scss";

const REDIRECT_SECONDS = 5;

const statusMessages: Record<string, { title: string; description: string }> = {
  success: {
    title: "Account Linked",
    description: "Your account has been connected successfully.",
  },
  failed: {
    title: "Linking Failed",
    description: "Something went wrong during the connection. Please try again.",
  },
  exist: {
    title: "Already Linked",
    description: "This account is already connected to your profile.",
  },
  mismatch: {
    title: "Account Conflict",
    description: "This account is already linked to a different user.",
  },
  joinfailed: {
    title: "Account Linked",
    description:
      "Your Discord account is connected. We couldn't add you to the server automatically — you can use the Rejoin button in settings.",
  },
  linked: {
    title: "Account Linked",
    description:
      "Your Discord account is connected. Grant the server-join permission to join the Hippocampus server.",
  },
  missingscope: {
    title: "Permission Needed",
    description:
      "We couldn't link Discord because a required permission wasn't granted. Please try again and accept all permissions.",
  },
};

// The link itself succeeded for these — only the automatic server join varies.
const LINK_SUCCESS_STATUSES = new Set(["success", "joinfailed", "linked"]);

export default function AccountLink() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshLinkedAccounts } = useLinkedAccounts();
  const [status, setStatus] = useState<string>("");
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get("status") || "";
    setStatus(result);

    if (LINK_SUCCESS_STATUSES.has(result)) {
      refreshLinkedAccounts();
    }
  }, [location.search]);

  useEffect(() => {
    if (!status) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate("/settings");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status, navigate]);

  const isSuccess = LINK_SUCCESS_STATUSES.has(status);
  const message = statusMessages[status] || statusMessages.failed;

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={`${styles.iconWrapper} ${isSuccess ? styles.successIcon : styles.failIcon}`}>
          {isSuccess ? (
            <svg className={styles.checkmark} viewBox="0 0 52 52">
              <circle className={styles.checkmarkCircle} cx="26" cy="26" r="25" fill="none" />
              <path className={styles.checkmarkCheck} fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
            </svg>
          ) : (
            <svg className={styles.xmark} viewBox="0 0 52 52">
              <circle className={styles.xmarkCircle} cx="26" cy="26" r="25" fill="none" />
              <path className={styles.xmarkX} fill="none" d="M16 16 36 36 M36 16 16 36" />
            </svg>
          )}
        </div>

        <h1 className={isSuccess ? styles.successTitle : styles.failTitle}>
          {message.title}
        </h1>
        <p className={styles.description}>{message.description}</p>

        <p className={styles.redirect}>
          Redirecting to settings in {countdown}s
        </p>

        <button
          className={styles.settingsBtn}
          onClick={() => navigate("/settings")}
        >
          Go to Settings
        </button>
      </div>
    </div>
  );
}
